import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Users, ExternalLink, AlertTriangle, MessageSquare, FileText, Search, X, Upload, Eye, File } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Clientes = () => {
  const navigate = useNavigate();
  const { propiedadSeleccionada } = usePropiedad();
  const [clientes, setClientes] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [clienteAEliminar, setClienteAEliminar] = useState(null);
  const [archivoCedula, setArchivoCedula] = useState(null);
  const [subiendoCedula, setSubiendoCedula] = useState(false);
  const [documentosCliente, setDocumentosCliente] = useState([]);
  const [modalDocumentos, setModalDocumentos] = useState(false);
  const [clienteDocumentos, setClienteDocumentos] = useState(null);
  const [nuevoDocumento, setNuevoDocumento] = useState({ archivo: null, tipo: 'Documento', descripcion: '' });
  const [subiendoDocumento, setSubiendoDocumento] = useState(false);
  const [formData, setFormData] = useState({
    propiedad_id: '',
    nombre: '',
    apellido: '',
    rut: '',
    direccion: '',
    telefono: '',
    email: '',
    cedula_url: null,
    notas: ''
  });

  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatos();
    }
  }, [propiedadSeleccionada]);

  const cargarDatos = async () => {
    if (!propiedadSeleccionada) return;
    
    try {
      const [resClientes, resReservas] = await Promise.all([
        axios.get(`${API}/clientes?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/reservas?propiedad_id=${propiedadSeleccionada.id_propiedad}`)
      ]);
      setClientes(resClientes.data);
      setReservas(resReservas.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const abrirModal = (cliente = null) => {
    if (cliente) {
      setClienteEditando(cliente);
      
      // Si el cliente tiene apellido, usar directamente
      // Si no tiene apellido pero tiene espacio en nombre, intentar separar
      let nombre = cliente.nombre;
      let apellido = cliente.apellido || '';
      
      if (!apellido && nombre && nombre.includes(' ')) {
        const partes = nombre.split(' ', 2);
        nombre = partes[0];
        apellido = partes[1] || '';
      }
      
      setFormData({
        propiedad_id: cliente.propiedad_id || propiedadSeleccionada?.id_propiedad || '',
        nombre: nombre,
        apellido: apellido,
        rut: cliente.rut || '',
        direccion: cliente.direccion || '',
        telefono: cliente.telefono || '',
        email: cliente.email || '',
        cedula_url: cliente.cedula_url || null,
        notas: cliente.notas || ''
      });
    } else {
      setClienteEditando(null);
      setFormData({
        propiedad_id: propiedadSeleccionada?.id_propiedad || '',
        nombre: '',
        apellido: '',
        rut: '',
        direccion: '',
        telefono: '',
        email: '',
        cedula_url: null,
        notas: ''
      });
    }
    setArchivoCedula(null);
    setModalAbierto(true);
  };

  const subirArchivoCedula = async () => {
    if (!archivoCedula) return null;
    
    try {
      setSubiendoCedula(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', archivoCedula);
      
      const response = await axios.post(`${API}/upload-cedula`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSubiendoCedula(false);
      return response.data.url;
    } catch (error) {
      setSubiendoCedula(false);
      toast.error('Error al subir archivo de cédula');
      return null;
    }
  };

  const guardarCliente = async () => {
    // Validar que el nombre no esté vacío
    if (!formData.nombre || formData.nombre.trim() === '') {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      // Si hay un nuevo archivo de cédula, subirlo primero
      let cedulaUrl = formData.cedula_url;
      if (archivoCedula) {
        cedulaUrl = await subirArchivoCedula();
        if (!cedulaUrl) return; // Si falló la subida, no continuar
      }

      const datosCliente = {
        ...formData,
        cedula_url: cedulaUrl
      };

      if (clienteEditando) {
        await axios.put(`${API}/clientes/${clienteEditando.id_cliente}`, datosCliente);
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/clientes`, datosCliente);
        toast.success('Cliente creado');
      }
      cargarDatos();
      setModalAbierto(false);
    } catch (error) {
      toast.error('Error al guardar cliente');
    }
  };

  const confirmarEliminacion = (cliente) => {
    setClienteAEliminar(cliente);
  };

  const eliminarCliente = async () => {
    if (!clienteAEliminar) return;
    
    try {
      await axios.delete(`${API}/clientes/${clienteAEliminar.id_cliente}`);
      toast.success('Cliente eliminado exitosamente');
      setClienteAEliminar(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar el cliente');
      console.error('Error:', error);
    }
  };

  const contarReservasCliente = (nombreCliente) => {
    return reservas.filter(r => r.huesped_nombre.toLowerCase() === nombreCliente.toLowerCase()).length;
  };

  const verReservasCliente = (nombreCliente) => {
    navigate(`/admin/reservas?cliente=${encodeURIComponent(nombreCliente)}`);
  };

  const verContratosCliente = (clienteId) => {
    navigate(`/admin/contratos?cliente=${clienteId}`);
  };

  // Abrir modal de documentos
  const abrirModalDocumentos = async (cliente) => {
    setClienteDocumentos(cliente);
    setDocumentosCliente(cliente.documentos || []);
    setModalDocumentos(true);
  };

  // Subir nuevo documento
  const subirDocumento = async () => {
    if (!nuevoDocumento.archivo || !clienteDocumentos) return;
    
    setSubiendoDocumento(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', nuevoDocumento.archivo);
      formDataUpload.append('tipo', nuevoDocumento.tipo);
      formDataUpload.append('descripcion', nuevoDocumento.descripcion || nuevoDocumento.archivo.name);
      
      const res = await axios.post(
        `${API}/clientes/${clienteDocumentos.id_cliente}/documentos`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setDocumentosCliente([...documentosCliente, res.data]);
      setNuevoDocumento({ archivo: null, tipo: 'Documento', descripcion: '' });
      toast.success('Documento subido correctamente');
      cargarDatos(); // Refresh clients list
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setSubiendoDocumento(false);
    }
  };

  // Eliminar documento
  const eliminarDocumento = async (documentoId) => {
    if (!clienteDocumentos) return;
    
    try {
      await axios.delete(`${API}/clientes/${clienteDocumentos.id_cliente}/documentos/${documentoId}`);
      setDocumentosCliente(documentosCliente.filter(d => d.id !== documentoId));
      toast.success('Documento eliminado');
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  // Filtrar clientes por búsqueda
  const clientesFiltrados = clientes.filter(cliente => {
    if (!busqueda.trim()) return true;
    
    const searchLower = busqueda.toLowerCase().trim();
    const campos = [
      cliente.nombre,
      cliente.apellido,
      cliente.email,
      cliente.telefono,
      cliente.rut,
      cliente.direccion,
      cliente.notas
    ];
    
    return campos.some(campo => 
      campo && campo.toString().toLowerCase().includes(searchLower)
    );
  });


  return (
    <div className="space-y-6 fade-in-up">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Clientes
          </h1>
          <p className="text-[#0E3A53]/70">Gestiona la información de tus huéspedes</p>
        </div>
        <div className="flex gap-2">
          <Button 
            data-testid="btn-mensajes-whatsapp"
            onClick={() => navigate('/mensajes-whatsapp')}
            variant="outline"
            className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            Enviar WhatsApp
          </Button>
          <Button 
            data-testid="add-cliente-btn"
            onClick={() => abrirModal()}
            className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:shadow-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Search Box */}
      <div className="card-beach">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#0E3A53]/50" />
            <Input
              type="text"
              placeholder="Buscar por nombre, apellido, teléfono, email, RUT..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10 pr-10"
              data-testid="search-clientes"
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#0E3A53]/50 hover:text-[#0E3A53]"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="text-sm text-[#0E3A53]/60">
            Mostrando <span className="font-semibold text-[#1AA1D8]">{clientesFiltrados.length}</span> de{' '}
            <span className="font-semibold">{clientes.length}</span> clientes
          </div>
        </div>
      </div>

      <div className="card-beach">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#8FE0FF]">
                <th className="text-left p-3 text-[#0E3A53] font-semibold">Nombre</th>
                <th className="text-left p-3 text-[#0E3A53] font-semibold">Apellido</th>
                <th className="text-left p-3 text-[#0E3A53] font-semibold hidden md:table-cell">Contacto</th>
                <th className="text-left p-3 text-[#0E3A53] font-semibold hidden lg:table-cell">RUT</th>
                <th className="text-center p-3 text-[#0E3A53] font-semibold hidden sm:table-cell">Reservas</th>
                <th className="text-center p-3 text-[#0E3A53] font-semibold hidden md:table-cell">Docs</th>
                <th className="text-right p-3 text-[#0E3A53] font-semibold hidden sm:table-cell">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {clientesFiltrados.map((cliente) => {
                const nombreCompleto = `${cliente.nombre} ${cliente.apellido}`.trim();
                const numReservas = contarReservasCliente(nombreCompleto);
                return (
                  <tr 
                    key={cliente.id_cliente}
                    className="border-b border-[#8FE0FF]/30 hover:bg-[#8FE0FF]/10 transition-colors"
                    data-testid={`cliente-row-${cliente.id_cliente}`}
                  >
                    {/* Nombre */}
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1AA1D8] to-[#0E3A53] flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {cliente.nombre?.charAt(0)}{cliente.apellido?.charAt(0) || ''}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-semibold text-[#0E3A53] truncate">
                                {cliente.nombre}
                              </p>
                              {/* Info móvil */}
                              <div className="md:hidden">
                                {cliente.apellido && (
                                  <p className="text-xs text-[#0E3A53]/70">{cliente.apellido}</p>
                                )}
                                {cliente.telefono && (
                                  <p className="text-xs text-[#0E3A53]/50">{cliente.telefono}</p>
                                )}
                              </div>
                            </div>
                            {/* Acciones móviles integradas */}
                            <div className="flex gap-0.5 sm:hidden flex-shrink-0">
                              <button
                                onClick={() => verReservasCliente(nombreCompleto)}
                                className="p-1 hover:bg-[#1AA1D8]/20 rounded"
                                title="Reservas"
                              >
                                <ExternalLink className="w-4 h-4 text-[#1AA1D8]" />
                              </button>
                              <button
                                onClick={() => abrirModal(cliente)}
                                className="p-1 hover:bg-[#1AA1D8]/20 rounded"
                                title="Editar"
                              >
                                <Edit className="w-4 h-4 text-[#1AA1D8]" />
                              </button>
                              <button
                                onClick={() => confirmarEliminacion(cliente)}
                                className="p-1 hover:bg-[#FF7F6A]/20 rounded"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4 text-[#FF7F6A]" />
                              </button>
                            </div>
                          </div>
                          {cliente.notas && (
                            <p className="text-xs text-[#0E3A53]/50 truncate max-w-[200px] hidden sm:block" title={cliente.notas}>
                              {cliente.notas}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Apellido */}
                    <td className="p-3">
                      <span className="text-[#0E3A53]">{cliente.apellido || '-'}</span>
                    </td>

                    {/* Contacto */}
                    <td className="p-3 hidden md:table-cell">
                      <div className="space-y-1">
                        {cliente.telefono && (
                          <p className="text-sm text-[#0E3A53] flex items-center gap-1">
                            <span className="text-[#1AA1D8]">📱</span> {cliente.telefono}
                          </p>
                        )}
                        {cliente.email && (
                          <p className="text-sm text-[#0E3A53]/70 truncate max-w-[200px]" title={cliente.email}>
                            {cliente.email}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* RUT */}
                    <td className="p-3 hidden lg:table-cell">
                      <span className="text-sm text-[#0E3A53]">{cliente.rut || '-'}</span>
                    </td>

                    {/* Reservas */}
                    <td className="p-3 text-center hidden sm:table-cell">
                      <button
                        data-testid={`ver-reservas-${cliente.id_cliente}`}
                        onClick={() => verReservasCliente(nombreCompleto)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#1AA1D8]/10 text-[#1AA1D8] rounded-full text-sm font-medium hover:bg-[#1AA1D8]/20 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {numReservas}
                      </button>
                    </td>

                    {/* Documentos */}
                    <td className="p-3 text-center hidden md:table-cell">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`ver-documentos-${cliente.id_cliente}`}
                          onClick={() => abrirModalDocumentos(cliente)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors"
                          title="Ver documentos"
                        >
                          <File className="w-3 h-3" />
                          {(cliente.documentos?.length || 0) + (cliente.cedula_url ? 1 : 0)}
                        </button>
                        <button
                          data-testid={`ver-contratos-${cliente.id_cliente}`}
                          onClick={() => verContratosCliente(cliente.id_cliente)}
                          className="p-1 text-cyan-600 hover:bg-cyan-100 rounded"
                          title="Ver contratos"
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      </div>
                    </td>

                    {/* Acciones - Desktop */}
                    <td className="p-3 hidden sm:table-cell">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`edit-cliente-${cliente.id_cliente}`}
                          onClick={() => abrirModal(cliente)}
                          className="p-2 hover:bg-[#1AA1D8]/20 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4 text-[#1AA1D8]" />
                        </button>
                        <button
                          data-testid={`delete-cliente-${cliente.id_cliente}`}
                          onClick={() => confirmarEliminacion(cliente)}
                          className="p-2 hover:bg-[#FF7F6A]/20 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-[#FF7F6A]" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {clientesFiltrados.length === 0 && clientes.length > 0 && (
          <div className="text-center py-12">
            <Search className="w-24 h-24 text-[#1AA1D8]/30 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-[#0E3A53] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              No se encontraron resultados
            </h3>
            <p className="text-[#0E3A53]/70">Intenta con otros términos de búsqueda</p>
            <Button 
              variant="outline" 
              className="mt-4 border-[#1AA1D8] text-[#1AA1D8]"
              onClick={() => setBusqueda('')}
            >
              Limpiar búsqueda
            </Button>
          </div>
        )}

        {clientes.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-24 h-24 text-[#1AA1D8]/30 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-[#0E3A53] mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              No hay clientes registrados
            </h3>
            <p className="text-[#0E3A53]/70">Agrega tu primer cliente para comenzar</p>
          </div>
        )}
      </div>

      {/* Alert Dialog de Confirmación */}
      <AlertDialog open={!!clienteAEliminar} onOpenChange={() => setClienteAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#FF7F6A]">
              <AlertTriangle className="w-6 h-6" />
              ¿Eliminar este cliente?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el cliente:
              {clienteAEliminar && (
                <div className="mt-2 p-3 bg-[#F7E8C9] rounded-lg">
                  <p className="font-semibold text-[#0E3A53]">{clienteAEliminar.nombre} {clienteAEliminar.apellido}</p>
                  {clienteAEliminar.email && (
                    <p className="text-sm text-[#0E3A53]/70">{clienteAEliminar.email}</p>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-cliente">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-cliente"
              onClick={eliminarCliente}
              className="bg-[#FF7F6A] hover:bg-[#FF7F6A]/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal */}
      <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {clienteEditando ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                data-testid="input-nombre-cliente"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Juan"
              />
            </div>
            <div>
              <Label htmlFor="apellido">Apellido</Label>
              <Input
                id="apellido"
                data-testid="input-apellido-cliente"
                value={formData.apellido}
                onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                placeholder="Pérez"
              />
            </div>
            <div>
              <Label htmlFor="rut">RUT</Label>
              <Input
                id="rut"
                data-testid="input-rut-cliente"
                value={formData.rut}
                onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                placeholder="12.345.678-9"
              />
            </div>
            <div>
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                data-testid="input-direccion-cliente"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Calle Principal 123"
              />
            </div>
            <div>
              <Label htmlFor="telefono">Teléfono</Label>
              <Input
                id="telefono"
                data-testid="input-telefono-cliente"
                type="tel"
                placeholder="+56 9 1234 5678"
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="input-email-cliente"
                type="email"
                placeholder="correo@ejemplo.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="cedula">Cédula (Archivo)</Label>
              <div className="space-y-2">
                <Input
                  id="cedula"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setArchivoCedula(e.target.files[0])}
                  disabled={subiendoCedula}
                />
                {formData.cedula_url && !archivoCedula && (
                  <div className="flex items-center gap-2 text-sm text-cyan-600">
                    <FileText className="w-4 h-4" />
                    <a 
                      href={`${BACKEND_URL}${formData.cedula_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Ver cédula actual
                    </a>
                  </div>
                )}
                {archivoCedula && (
                  <p className="text-sm text-gray-600">
                    Nuevo archivo: {archivoCedula.name}
                  </p>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notas">Notas</Label>
              <Input
                id="notas"
                data-testid="input-notas-cliente"
                placeholder="Información adicional del cliente"
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setModalAbierto(false)} data-testid="cancel-cliente-btn">
              Cancelar
            </Button>
            <Button 
              onClick={guardarCliente}
              data-testid="save-cliente-btn"
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
            >
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Documentos */}
      <Dialog open={modalDocumentos} onOpenChange={setModalDocumentos}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Documentos de {clienteDocumentos?.nombre} {clienteDocumentos?.apellido}
            </DialogTitle>
          </DialogHeader>

          {/* Lista de documentos existentes */}
          <div className="space-y-3">
            <h3 className="font-semibold text-[#0E3A53] flex items-center gap-2">
              <File className="w-4 h-4" />
              Documentos guardados ({documentosCliente.length + (clienteDocumentos?.cedula_url ? 1 : 0)})
            </h3>
            
            {/* Cédula original si existe */}
            {clienteDocumentos?.cedula_url && (
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-[#0E3A53]">Cédula de Identidad</p>
                    <p className="text-xs text-gray-500">Documento principal</p>
                  </div>
                </div>
                <a
                  href={`${BACKEND_URL}${clienteDocumentos.cedula_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg"
                >
                  <Eye className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* Otros documentos */}
            {documentosCliente.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#1AA1D8]" />
                  <div>
                    <p className="font-medium text-[#0E3A53]">{doc.tipo}</p>
                    <p className="text-xs text-gray-500">
                      {doc.descripcion || doc.nombre_archivo} 
                      {doc.origen === 'solicitud_reserva' && ' • Desde solicitud'}
                    </p>
                    {doc.fecha_subida && (
                      <p className="text-xs text-gray-400">
                        {new Date(doc.fecha_subida).toLocaleDateString('es-CL')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href={`${BACKEND_URL}${doc.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#1AA1D8] hover:bg-[#1AA1D8]/10 rounded-lg"
                    title="Ver documento"
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => eliminarDocumento(doc.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    title="Eliminar documento"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {documentosCliente.length === 0 && !clienteDocumentos?.cedula_url && (
              <p className="text-center text-gray-500 py-4">No hay documentos guardados</p>
            )}
          </div>

          {/* Agregar nuevo documento */}
          <div className="mt-6 pt-4 border-t">
            <h3 className="font-semibold text-[#0E3A53] mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Agregar nuevo documento
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Tipo de documento</Label>
                <Select
                  value={nuevoDocumento.tipo}
                  onValueChange={(value) => setNuevoDocumento({ ...nuevoDocumento, tipo: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cédula de Identidad">Cédula de Identidad</SelectItem>
                    <SelectItem value="Pasaporte">Pasaporte</SelectItem>
                    <SelectItem value="Contrato firmado">Contrato firmado</SelectItem>
                    <SelectItem value="Comprobante de pago">Comprobante de pago</SelectItem>
                    <SelectItem value="Otro documento">Otro documento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción (opcional)</Label>
                <Input
                  value={nuevoDocumento.descripcion}
                  onChange={(e) => setNuevoDocumento({ ...nuevoDocumento, descripcion: e.target.value })}
                  placeholder="Descripción del documento"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Archivo</Label>
                <Input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={(e) => setNuevoDocumento({ ...nuevoDocumento, archivo: e.target.files[0] })}
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button
                onClick={subirDocumento}
                disabled={!nuevoDocumento.archivo || subiendoDocumento}
                className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
              >
                {subiendoDocumento ? 'Subiendo...' : 'Subir documento'}
              </Button>
            </div>
          </div>

          <div className="flex justify-end mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setModalDocumentos(false)}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clientes;
